/**
 * Lab basket types (PROD-572).
 *
 * Implements ADR-001 correction 1: the prior prototype "Dr. Al-Dhaheri
 * always orders TSH alongside standard TLP1 labs. TSH now auto-included
 * in basket without NBA prompt." was auto-execute behavior outside ADR-001
 * Tier 3 scope (`send_educational_message` only). The corrected behavior:
 * TSH is pre-selected in the lab basket, and a one-click clinician
 * confirmation is required before the lab order is placed.
 *
 * Sources of truth:
 *   - master prompt v3.3 §Architecture Constraint correction 1
 *   - SPEC §2.2 layer enforcement table + correction 1
 *   - COMPONENT-CONTRACTS §9 NbaCard NC-9
 *
 * HC5: zero em / en dashes anywhere. Section separators use `-` (hyphen),
 * commas, periods, or "and" / "to" instead.
 *
 * Telemetry (HC9): when a lab basket is rendered with
 * `requiresConfirmation` items, the consuming view emits a
 * `lab_basket_confirmation_required` event listing the LOINC codes
 * pending confirmation. Confirming a single item emits
 * `lab_basket_item_confirmed`.
 */
import { z } from 'zod';

/**
 * Single lab item in a basket. The shape mirrors the `Nba` fixture's lab
 * record (`src/data/synthetic-patients/types.ts` Labs schema) but is
 * scoped to ordering rather than results display.
 *
 * `code` carries the LOINC short code where available (e.g. `TSH`,
 * `HBA1C`); the canonical full LOINC code may be added via an optional
 * `loincCode` field in a follow-up ticket.
 */
export const labBasketItemSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  /**
   * If true, the item appears in the basket pre-checked but the order
   * does NOT execute until the clinician confirms. Pre-selection is the
   * UX affordance that replaces the prior auto-include behavior.
   */
  preselected: z.boolean(),
  /**
   * If true, the basket cannot dispatch the order without an explicit
   * one-click clinician confirmation. ADR-001 Layer 3 + HC8: no
   * auto-execute outside the narrow Tier 3 scope.
   */
  requiresConfirmation: z.boolean(),
  /**
   * ISO timestamp of confirmation, or `null` if pending. The view
   * surfaces a confirmation pill and a "Confirm" button while this is
   * `null`.
   */
  confirmedAt: z.string().min(1).nullable(),
  /**
   * Optional rationale for why the item was preselected. Surfaced in the
   * NBA card / basket tooltip so the clinician can decide quickly.
   */
  rationale: z.string().min(1).optional(),
});

export type LabBasketItem = z.infer<typeof labBasketItemSchema>;

/**
 * Lab basket attached to a pathway state. Carries the active items plus
 * the canonical pathway / step / tenant context for telemetry routing.
 */
export const labBasketSchema = z.object({
  pathwayId: z.string().min(1),
  stepId: z.string().min(1),
  tenantId: z.string().min(1),
  items: z.array(labBasketItemSchema),
});

export type LabBasket = z.infer<typeof labBasketSchema>;

/**
 * Convenience predicate: any items in the basket still require
 * clinician confirmation. View consumers use this to decide whether
 * the "Confirm and Order" CTA is enabled.
 */
export function basketRequiresConfirmation(basket: LabBasket): boolean {
  return basket.items.some(
    (item) => item.requiresConfirmation && item.confirmedAt === null,
  );
}

/**
 * Convenience predicate: an individual item still needs confirmation.
 */
export function itemRequiresConfirmation(item: LabBasketItem): boolean {
  return item.requiresConfirmation && item.confirmedAt === null;
}
